<template>
  <div
    v-if="visible"
    ref="rootEl"
    data-testid="email-verification-banner"
    :style="{ '--ev-feedback-height': `${feedbackHeightPx}px` }"
  >
    <AlertBanner
      v-if="feedbackMessage"
      :status="feedbackStatus"
      class="banner-below-nav !leading-10"
      :full-page-banner="true"
      :auto-scroll-to-banner="false"
      data-testid="email-verification-banner-feedback"
    >
      <span class="inline-block px-16 py-8">{{ feedbackMessage }}</span>
    </AlertBanner>
    <AlertBanner
      :status="helperStatus.Warn"
      class="banner-below-nav !leading-10"
      :class="{ 'banner-below-feedback': !!feedbackMessage }"
      :full-page-banner="true"
      :auto-scroll-to-banner="false"
    >
      <span class="inline-block px-16 py-8">
        Verify your email with the link sent to
        <span class="font-medium" data-testid="email-verification-banner-address">{{ displayedEmail }}</span
        >.
        <button
          type="button"
          class="underline font-medium cursor-pointer"
          data-testid="email-verification-banner-resend"
          :disabled="resending"
          @click="resendVerificationEmail"
        >
          Resend link
        </button>
      </span>
    </AlertBanner>
    <!-- Matches the fixed banners' measured height (wraps taller on narrow viewports). -->
    <div class="w-full" :style="{ height: `${totalHeightPx}px` }" aria-hidden="true">&nbsp;</div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import helperStatus from '@/helpers/status';
import shouldShowEmailVerificationBanner from '@/helpers/emailVerificationBannerLogic';
import { shouldForceParentExperimentEmailVerificationBanner } from '@/helpers/shouldForceParentExperimentEmailVerificationBanner';
import { EMAIL_VERIFICATION_STATUS } from '@/graphql/account/queries';
import { SEND_VERIFICATION_EMAIL } from '@/graphql/account/mutations';
import { EMAIL_VERIFICATION_POLL_INTERVAL_MS } from '@/apps/verifyEmail/useVerifyEmailWaiting';
import { invalidateEmailVerificationStatus } from '@/apps/verifyEmail/helpers/emailVerificationStatus';
import { getSharedApolloClient } from '@/lib/apollo_setup';

defineOptions({ name: 'EmailVerificationBanner' });

const emit = defineEmits<{
  'visibility-change': [visible: boolean];
  'feedback-visible': [visible: boolean];
  'offset-height': [heightPx: number];
}>();

type GraphQLErrorLike = {
  message: string;
  extensions?: { error_code?: string };
};

/** Fallback when layout hasn't been measured yet (leading-10 + py-8). */
const DEFAULT_BANNER_HEIGHT_PX = 56;

const route = useRoute();
const apollo = getSharedApolloClient();

const rootEl = ref<HTMLElement | null>(null);
const showBannerFromApi = ref(false);
const forceShowFromExperiment = ref(false);
const email = ref('');
const loaded = ref(false);
const resending = ref(false);
const feedbackMessage = ref('');
const feedbackStatus = ref(helperStatus.Valid);
const pollTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const wasVisible = ref(false);
const feedbackHeightPx = ref(DEFAULT_BANNER_HEIGHT_PX);
const totalHeightPx = ref(DEFAULT_BANNER_HEIGHT_PX);

let resizeObserver: ResizeObserver | null = null;

const visible = computed(() => {
  if (!loaded.value) {
    return false;
  }
  return shouldShowEmailVerificationBanner(showBannerFromApi.value, route.path, {
    forceShow: forceShowFromExperiment.value,
  });
});

const displayedEmail = computed(() => email.value || 'your email address');

function measureHeights(): void {
  const root = rootEl.value;
  if (!root) {
    return;
  }

  const banners = root.querySelectorAll<HTMLElement>('.banner-below-nav');
  let feedback = 0;
  let total = 0;
  banners.forEach((banner, index) => {
    const height = banner.offsetHeight || DEFAULT_BANNER_HEIGHT_PX;
    total += height;
    if (feedbackMessage.value && index === 0) {
      feedback = height;
    }
  });

  if (total === 0) {
    total = feedbackMessage.value ? DEFAULT_BANNER_HEIGHT_PX * 2 : DEFAULT_BANNER_HEIGHT_PX;
  }
  if (feedbackMessage.value && feedback === 0) {
    feedback = DEFAULT_BANNER_HEIGHT_PX;
  }

  feedbackHeightPx.value = feedback || DEFAULT_BANNER_HEIGHT_PX;
  totalHeightPx.value = total;
  emit('offset-height', total);
}

function observeHeights(): void {
  resizeObserver?.disconnect();
  resizeObserver = null;

  const root = rootEl.value;
  if (!root || typeof ResizeObserver === 'undefined') {
    measureHeights();
    return;
  }

  resizeObserver = new ResizeObserver(() => {
    measureHeights();
  });
  root.querySelectorAll('.banner-below-nav').forEach((banner) => {
    resizeObserver?.observe(banner);
  });
  measureHeights();
}

function clearFeedback(): void {
  feedbackMessage.value = '';
}

function showFeedback(message: string, status: number): void {
  feedbackMessage.value = message;
  feedbackStatus.value = status;
}

function stopPolling(): void {
  if (pollTimer.value !== null) {
    clearTimeout(pollTimer.value);
    pollTimer.value = null;
  }
}

function scheduleNextPoll(): void {
  if (!visible.value) {
    return;
  }
  stopPolling();
  pollTimer.value = setTimeout(() => {
    void pollOnce();
  }, EMAIL_VERIFICATION_POLL_INTERVAL_MS);
}

async function refreshStatus(): Promise<void> {
  try {
    const { data } = await apollo.query({
      query: EMAIL_VERIFICATION_STATUS,
      fetchPolicy: 'network-only',
    });
    const status = data?.emailVerificationStatus;
    const verified = !!status?.verified;
    showBannerFromApi.value = !!status?.showBanner;
    forceShowFromExperiment.value = shouldForceParentExperimentEmailVerificationBanner(
      verified,
      !!status?.emailVerificationRequired
    );
    if (status?.email) {
      email.value = status.email;
    }
  } catch (error) {
    console.error('Error checking email verification banner eligibility:', error);
    showBannerFromApi.value = false;
    forceShowFromExperiment.value = false;
  } finally {
    loaded.value = true;
  }
}

async function pollOnce(): Promise<void> {
  if (!visible.value) {
    return;
  }
  await refreshStatus();
  if (visible.value) {
    scheduleNextPoll();
  }
}

function onWindowFocus(): void {
  void refreshStatus();
}

async function resendVerificationEmail(): Promise<void> {
  if (resending.value) {
    return;
  }

  resending.value = true;
  try {
    await apollo.mutate({
      mutation: SEND_VERIFICATION_EMAIL,
    });
    showFeedback(
      "Verification email re-sent. Check your email to verify and don't forget to check your spam folder.",
      helperStatus.Valid
    );
  } catch (error: unknown) {
    const graphqlError = (error as { graphQLErrors?: GraphQLErrorLike[] })?.graphQLErrors?.[0];
    if (graphqlError?.extensions?.error_code === 'rate_limit_exceeded') {
      showFeedback(graphqlError.message, helperStatus.Error);
    } else {
      showFeedback('Something went wrong sending your verification email. Please try again.', helperStatus.Error);
      console.error(error);
    }
  } finally {
    resending.value = false;
  }
}

watch(
  () => route.fullPath,
  () => {
    // Match Rails flash: clear on navigation, keep until then.
    clearFeedback();
    void refreshStatus();
  }
);

watch(feedbackMessage, (message) => {
  emit('feedback-visible', !!message);
  void nextTick(() => observeHeights());
});

watch(
  visible,
  (isVisible) => {
    emit('visibility-change', isVisible);
    if (isVisible) {
      wasVisible.value = true;
      scheduleNextPoll();
      void nextTick(() => observeHeights());
      return;
    }
    if (wasVisible.value) {
      invalidateEmailVerificationStatus();
      wasVisible.value = false;
    }
    clearFeedback();
    stopPolling();
    resizeObserver?.disconnect();
    resizeObserver = null;
    totalHeightPx.value = 0;
    emit('offset-height', 0);
  },
  { immediate: true }
);

onMounted(() => {
  void refreshStatus();
  window.addEventListener('focus', onWindowFocus);
});

onBeforeUnmount(() => {
  window.removeEventListener('focus', onWindowFocus);
  clearFeedback();
  stopPolling();
  resizeObserver?.disconnect();
  resizeObserver = null;
  emit('visibility-change', false);
  emit('feedback-visible', false);
  emit('offset-height', 0);
});
</script>

<style scoped lang="scss">
/* Stack the EV prompt beneath the temporary resend feedback banner (Rails: flash then EV). */
:deep(.banner-below-feedback) {
  top: calc(48px + var(--ev-feedback-height, 3.5rem));

  @media (min-width: 768px) {
    top: calc(80px + var(--ev-feedback-height, 3.5rem));
  }
}
</style>
